import { useVirtualizer } from "@tanstack/react-virtual";
import { useRef } from "react";

import type { ActivityItem } from "@/lib/api";
import { cn } from "@/lib/utils";

const KIND_DOT: Record<string, string> = {
  reprice_auto_applied: "dot-ok",
  reprice_manual: "dot-ok",
  reprice_applied: "dot-ok",
  refresh_completed: "dot-info",
  refresh_started: "dot-info",
  refresh_job_started: "dot-info",
  refresh_job_completed: "dot-info",
  refresh_partial: "dot-warn",
  refresh_skipped: "dot-warn",
  refresh_marketplace_started: "dot-info",
  refresh_marketplace_completed: "dot-info",
  refresh_marketplace_failed: "dot-err",
  refresh_failed: "dot-err",
  reprice_failed: "dot-err",
  poleepo_write_failed: "dot-err",
  mirakl_write_failed: "dot-err",
  mirakl_write_success: "dot-ok",
  reprice_auto_skipped: "dot-warn",
  publication_sync_started: "dot-info",
  publication_sync_completed: "dot-ok",
  publication_sync_failed: "dot-err",
  credentials_updated: "dot-info",
  auto_apply_toggled: "dot-warn",
  buybox_delta_changed: "dot-warn",
  min_margin_changed: "dot-warn",
};

interface ActivityTableProps {
  items: ActivityItem[];
  onRowClick: (item: ActivityItem) => void;
}

const ROW_HEIGHT = 56;

export function ActivityTable({
  items,
  onRowClick,
}: ActivityTableProps): JSX.Element {
  const parentRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });

  if (items.length === 0) {
    return (
      <div className="card-stack flex h-64 items-center justify-center bg-card text-sm text-ink-3">
        Nessuna attività trovata con i filtri correnti.
      </div>
    );
  }

  return (
    <div
      ref={parentRef}
      className="card-stack relative h-[640px] overflow-auto bg-card"
      data-testid="activity-table-scroll"
    >
      <div className="sticky top-0 z-10 grid grid-cols-[180px_240px_180px_140px_1fr] gap-3 border-b border-line bg-paper-2 px-4 py-2.5 text-label font-semibold text-ink-3">
        <span>Quando</span>
        <span>Tipo</span>
        <span>Marketplace</span>
        <span>SKU</span>
        <span>Messaggio</span>
      </div>
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          position: "relative",
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtual) => {
          const item = items[virtual.index];
          const dotClass = KIND_DOT[item.kind] ?? "dot-info";
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onRowClick(item)}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtual.start}px)`,
                height: virtual.size,
              }}
              className={cn(
                "grid grid-cols-[180px_240px_180px_140px_1fr] items-center gap-3 border-b border-line px-4 py-3 text-left text-sm transition hover:bg-key/15",
                virtual.index % 2 === 1 && "bg-paper-2",
              )}
              data-testid="activity-row"
            >
              <span className="font-mono tabular-nums text-xs text-ink-3">
                {new Date(item.ts).toLocaleString("it-IT")}
              </span>
              <span className="flex items-center gap-2 font-mono text-xs">
                <span
                  className={cn(
                    "inline-block h-2 w-2 rounded-full",
                    dotClass,
                  )}
                  aria-hidden="true"
                />
                <span className="truncate">{item.kind}</span>
              </span>
              <span className="truncate font-mono text-xs text-ink-3">
                {item.marketplace_id ?? "—"}
              </span>
              <span className="truncate font-mono text-xs">
                {item.sku ?? "—"}
              </span>
              <span className="truncate text-ink-2">{item.message}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
